// عناوين المدفوعات بالعربية. الرموز تبقى كما يعرفها الخادم.
import { EGP, egpNum, isWholeEGP } from '../../lib/format.ts';
export const PAYMENT_METHODS = [
  { code: 'INSTAPAY', ar: 'InstaPay' },
  { code: 'WALLET', ar: 'محفظة إلكترونية' },
  { code: 'CASH', ar: 'كاش' },
  { code: 'BANK_TRANSFER_SAR', ar: 'تحويل بنكي — ريال سعودي' },
  { code: 'VISA_OFFICE', ar: 'فيزا — المكتب' },
  { code: 'INDIRECT_INSTALLMENT', ar: 'تقسيط غير مباشر' },
  { code: 'CASH_USD', ar: 'دولار كاش' },
  { code: 'BANK_TRANSFER_USD', ar: 'تحويل بنكي — دولار أمريكي' },
] as const;

export type PaymentMethodCode = (typeof PAYMENT_METHODS)[number]['code'];
export type CurrencyCode = 'EGP' | 'SAR' | 'USD';

export const SAR = 'ريال سعودي';
export const USD = 'دولار أمريكي';
const CURRENCY_AR: Record<string, string> = { EGP, SAR, USD };

/**
 * عملة الطريقة — الخادم هو الذي يشتقّها ويكتبها، وهذه نسخة العرض لتُظهر الواجهة العملة
 * الصحيحة **قبل** الإرسال. لا تحويل ولا سعر صرف في أي اتجاه: الريال يُكتب ويُعرض ريالًا،
 * ولا يُجمع مع الجنيه في رقم واحد ولا يُخصم من المتبقي بالجنيه.
 */
const METHOD_CURRENCY: Record<string, CurrencyCode> = {
  BANK_TRANSFER_SAR: 'SAR',
  CASH_USD: 'USD', BANK_TRANSFER_USD: 'USD',
};
export const currencyOfMethod = (method: string): CurrencyCode => METHOD_CURRENCY[method] ?? 'EGP';

export const currencyAr = (currency: string | null | undefined): string =>
  CURRENCY_AR[currency ?? 'EGP'] ?? String(currency);

/** صيغة الجرّ للجُمل: «اكتب رقمًا صحيحًا بلا كسور **بالجنيه المصري**». */
const CURRENCY_IN_AR: Record<string, string> = {
  EGP: 'بالجنيه المصري', SAR: 'بالريال السعودي', USD: 'بالدولار الأمريكي',
};
export const currencyInAr = (currency: string | null | undefined): string =>
  CURRENCY_IN_AR[currency ?? 'EGP'] ?? currencyAr(currency);

/** مبلغ بعملته الصريحة — لا يُعرض رقم مالي في هذا النظام بلا اسم عملته. */
export function moneyIn(v: string | number | null | undefined, currency: string | null | undefined): string {
  const n = egpNum(v);
  return n === '—' ? n : `${n} ${currencyAr(currency)}`;
}

export const PAYMENT_STATUSES = [
  { code: 'PENDING_REVIEW', ar: 'بانتظار مراجعة الأدمن' },
  { code: 'VERIFIED', ar: 'تم التحقق' },
  { code: 'REJECTED', ar: 'مرفوضة' },
  { code: 'VOIDED', ar: 'ملغاة' },
] as const;

export const VOID_REQUEST_STATUSES = [
  { code: 'PENDING', ar: 'بانتظار قرار الأدمن' },
  { code: 'APPROVED', ar: 'معتمد' },
  { code: 'REJECTED', ar: 'مرفوض' },
] as const;

type Opt = { code: string; ar: string };
const lookup = (list: readonly Opt[]) => (code: string | null | undefined): string =>
  (code && list.find(o => o.code === code)?.ar) || '—';

export const methodAr = lookup(PAYMENT_METHODS);
export const paymentStatusAr = lookup(PAYMENT_STATUSES);
export const voidStatusAr = lookup(VOID_REQUEST_STATUSES);

export const paymentStatusTone: Record<string, string> = {
  PENDING_REVIEW: 'bg-amber-50 text-amber-800',
  VERIFIED: 'bg-emerald-600 text-white',
  REJECTED: 'bg-rose-50 text-rose-700',
  VOIDED: 'bg-slate-100 text-ink-500',
};

/** الدفعة «مفتوحة» = ما زالت في مسار العمل. المرفوضة نهائية والملغاة خرجت منه. */
export const isOpenPayment = (status: string): boolean => status === 'PENDING_REVIEW' || status === 'VERIFIED';

/** حقل حساب/رقم المصدر يختلف بحسب الطريقة (الكاش بلا حساب إطلاقًا). */
const SOURCE_LABELS: Record<string, string> = {
  WALLET: 'رقم محفظة المصدر',
  INSTAPAY: 'حساب/عنوان InstaPay المصدر',
  BANK_TRANSFER_SAR: 'بنك/حساب التحويل المصدر',
  VISA_OFFICE: 'رقم العملية أو آخر أربعة أرقام',
  INDIRECT_INSTALLMENT: 'جهة التقسيط ورقم العقد',
  BANK_TRANSFER_USD: 'بنك/حساب التحويل المصدر',
};
export const sourceLabel = (method: string): string | null => SOURCE_LABELS[method] ?? null;

/** الطريقة الوحيدة التي يلزمها رقم مصدر. البقية اختيارية، والكاش بلا حقل أصلًا. */
export const sourceRequired = (method: string): boolean => method === 'WALLET';

export const PAYMENT_PROOF_NOTE =
  'الإثبات اختياري وقت التسجيل، ومطلوب قبل أن يتمكن الأدمن من التحقق: صورة التحويل، أو الإيصال، أو إقرار الاستلام الموقّع في حالة الكاش.';

// القاعدة المعتمدة: الإدخال بالجنيه الصحيح — لا كسور في حقول المال.
export const MONEY_RE = { test: (v: string): boolean => isWholeEGP(v) };

/** عرض مبلغ: جنيه مصري بأرقام صحيحة — القيمة نفسها لا تُعاد حسابها، العرض وحده يتغيّر. */
export const money = egpNum;
